function mapApiMessage({ ID, role, content, created_at }) {
  return {
    id: ID,
    role,
    content,
    createdAt: created_at,
  };
}

function mapApiThread({
  ID,
  prompt_id,
  created_at,
  name = null,
  messages_count = 0,
  latest_message = null,
}) {
  return {
    id: ID,
    promptCode: prompt_id,
    title: name,
    createdAt: created_at,
    messagesCount: messages_count,
    latestMessage: latest_message ? mapApiMessage(latest_message) : null,
  };
}

function mapApiPrompt(prompt) {
  const { schema } = prompt;
  const parameters =
    schema?.parameters?.map((parameter) => ({
      name: parameter.name,
      in: parameter.in,
      required: parameter.required,
      type: parameter.schema?.type,
      description: parameter.schema?.description,
    })) ?? [];

  return convertObjectToCamelCase({
    ...prompt,
    parameters,
  });
}

function convertObjectToSnakeCase(obj) {
  const convertedPrompt = {};
  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      const newKey = key === 'id' ? 'ID' : key.replace(/([A-Z])/g, '_$1').toLowerCase();
      convertedPrompt[newKey] = obj[key];
    }
  }
  return convertedPrompt;
}

function convertObjectToCamelCase(obj) {
  const convertedPrompt = {};
  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      const newKey =
        key === 'ID' ? 'id' : key.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
      convertedPrompt[newKey] = obj[key];
    }
  }
  return convertedPrompt;
}

const DEFAULT_LANGUAGE = globalThis.navigator?.language?.substring(0, 2) || 'en';

class AiApiError extends Error {
  constructor(message, status, response) {
    super(message);
    this.name = 'AiApiError';
    this.status = status;
    this.response = response;
  }
}

class OCAISDK {
  constructor({
    rootUrl = 'https://ai-api-poc.openclassrooms.tech',
    language = DEFAULT_LANGUAGE,
    accessToken,
  } = {}) {
    this.rootUrl = rootUrl;
    this.language = language;
    this.accessToken = accessToken;
    this.responseInterceptors = {};
    this.requestInterceptors = [];
  }

  setRootUrl(rootUrl) {
    this.rootUrl = rootUrl;
    return this;
  }

  setLanguage(language) {
    this.language = language;
    return this;
  }

  setAccessToken(accessToken) {
    this.accessToken = accessToken;
    return this;
  }

  setResponseInterceptor(endpoint, method, interceptor) {
    this.responseInterceptors[endpoint] = this.responseInterceptors[endpoint] ?? {};
    this.responseInterceptors[endpoint][method] = interceptor;
    return this;
  }

  addRequestInterceptor(interceptor) {
    this.requestInterceptors.push(interceptor);
    return this;
  }

  async applyRequestInterceptors({ url, method, headers, body }) {
    return this.requestInterceptors.reduce(
      async (requestData, interceptor) => await interceptor(requestData),
      {
        url,
        method,
        headers,
        body,
      },
    );
  }

  async fetch(
    endpoint,
    { method = 'GET', headers = {}, queryParams = new URLSearchParams(), body } = {},
  ) {
    if (endpoint in this.responseInterceptors && method in this.responseInterceptors[endpoint]) {
      const mockedResponse = await this.responseInterceptors[endpoint][method]({
        queryParams,
        body,
        headers,
      });

      if (Boolean(mockedResponse)) {
        if (mockedResponse instanceof Response) {
          return mockedResponse;
        }

        return new Response(JSON.stringify(mockedResponse));
      }
    }

    const headersToSend = {
      'Accept-Language': this.language,
      Accept: 'application/json',
      Authorization: `Bearer ${this.accessToken}`,
      ...headers,
    };

    let bodyToSend = body;

    if (method !== 'GET' && method !== 'HEAD') {
      const isStringBody = typeof body === 'string';

      headersToSend['Content-Type'] = isStringBody ? 'text/plain' : 'application/json';

      if (isStringBody) {
        bodyToSend = bodyToSend ?? ' ';
      } else {
        bodyToSend = bodyToSend ? JSON.stringify(bodyToSend) : '{}';
      }
    } else {
      bodyToSend = undefined;
    }

    const {
      url: finalUrl,
      method: finalMethod,
      headers: finalHeaders,
      body: finalBody,
    } = await this.applyRequestInterceptors({
      url: `${this.rootUrl}${endpoint}${queryParams?.size > 0 ? `?${queryParams.toString()}` : ''}`,
      method,
      headers: headersToSend,
      body: bodyToSend,
    });

    return fetch(finalUrl, {
      method: finalMethod,
      headers: finalHeaders,
      credentials: 'include',
      body: finalBody,
    });
  }

  async prompt(
    promptCode,
    { input, parameters, threadId, onStreamData, onThreadCreated, onMessageCreated } = {},
  ) {
    let queryParams = new URLSearchParams();

    if (parameters) {
      Object.keys(parameters).forEach((key) => {
        if (key !== 'version') queryParams.append(`retrieval_map[${key}]`, parameters[key]);
        else queryParams.append(key, parameters[key]);
      });
    }

    if (threadId) {
      queryParams.append('thread_id', threadId);
    }

    const response = await this.fetch(`/prompts/${promptCode}/execute`, {
      method: 'POST',
      queryParams,
      headers: {
        Accept: Boolean(onStreamData) ? 'application/octet-stream' : 'application/json',
      },
      body: input ?? ' ',
    });

    if (!response.ok) {
      throw new AiApiError(
        `[OCAISDK]: Error while fetching prompt: ${response.statusText}`,
        response.status,
        response,
      );
    }

    if (!threadId && onThreadCreated) {
      onThreadCreated(parseInt(response.headers.get('thread-id')));
    }

    if (onMessageCreated) {
      onMessageCreated(parseInt(response.headers.get('message-id')));
    }

    if (!onStreamData) {
      const textResponse = await response.text();

      try {
        return JSON.parse(textResponse);
      } catch (e) {
        return textResponse;
      }
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder('utf-8');

    return new Promise((resolve) => {
      let chunks = '';

      const processStream = async ({ done, value }) => {
        if (done) {
          resolve(chunks);
          return;
        }

        const newChunk = decoder.decode(value, { stream: true });

        chunks += newChunk;
        onStreamData(newChunk);

        return reader.read().then(processStream);
      };

      reader
        .read()
        .then(processStream)
        .catch((e) => console.error('processStream', e));
    });
  }

  async rateEntity(entityType, entityId, isLiked) {
    const response = await this.fetch(`/${entityType}s/${entityId}/rate`, {
      method: 'PUT',
      body: {
        rating: isLiked ? 'liked' : 'disliked',
      },
    });

    const responseObject = await response.json();

    return {
      id: responseObject.ID,
      userId: responseObject.user_id,
      createdAt: responseObject.created_at,
      [`${entityType}Id`]: responseObject[`${entityType}_id`],
      isLiked: responseObject.rating === 'liked',
    };
  }

  async rateMessage(messageId, isLiked) {
    return this.rateEntity('message', messageId, isLiked);
  }

  async rateThread(threadId, isLiked) {
    return this.rateEntity('thread', threadId, isLiked);
  }

  async getUserThreads(userId, promptCodes = []) {
    const queryParams = new URLSearchParams();

    if (promptCodes.length > 0) {
      queryParams.append('prompt_ids', promptCodes.join(','));
    }

    const response = await this.fetch(`/users/${userId}/threads`, {
      queryParams,
    });

    const threads = await response.json();

    return threads.map(mapApiThread);
  }

  async getThread(threadId) {
    const response = await this.fetch(`/threads/${threadId}`);

    const thread = await response.json();

    return mapApiThread(thread);
  }

  async deleteThread(threadId) {
    const response = await this.fetch(`/threads/${threadId}`, { method: 'DELETE' });

    return response.json();
  }

  async setThreadTitle(threadId, title) {
    const response = await this.fetch(`/threads/${threadId}`, {
      method: 'PATCH',
      body: {
        name: title,
      },
    });

    const thread = await response.json();
    return mapApiThread(thread);
  }

  async getThreadMessages(threadId) {
    const response = await this.fetch(`/threads/${threadId}/messages`);

    const messages = await response.json();

    return messages.map(mapApiMessage);
  }

  async getPrompts(queryParams = {}) {
    const response = await this.fetch('/prompts', {
      queryParams,
    });
    if (!response.ok) {
      throw new AiApiError(
        `[OCAISDK]: Error while fetching prompts: ${response.statusText}`,
        response.status,
        response,
      );
    }

    const prompts = await response.json();

    return prompts && prompts.map(mapApiPrompt);
  }

  async getPrompt(promptCode) {
    const response = await this.fetch(`/prompts/${promptCode}`);

    const prompt = await response.json();

    return mapApiPrompt(prompt);
  }
  async publishPrompt(promptId, versionId) {
    const response = await this.fetch(
      `/prompts/${promptId}/version/${versionId}/status/published`,
      {
        method: 'POST',
      },
    );
    if (!response.ok) {
      throw new AiApiError(
        `[OCAISDK]: Error while updating prompt: ${response.statusText}`,
        response.status,
        response,
      );
    }
    const responseData = await response.json();
    return mapApiPrompt(responseData);
  }

  async draftPrompt(promptId, versionId) {
    const response = await this.fetch(`/prompts/${promptId}/version/${versionId}/status/draft`, {
      method: 'POST',
    });
    if (!response.ok) {
      throw new AiApiError(
        `[OCAISDK]: Error while updating prompt: ${response.statusText}`,
        response.status,
        response,
      );
    }
    const responseData = await response.json();
    return mapApiPrompt(responseData);
  }

  async createPrompt(prompt) {
    const response = await this.fetch(`/prompts`, {
      method: 'POST',
      body: convertObjectToSnakeCase(prompt),
    });
    const updatedPrompt = await response.json();
    return mapApiPrompt(updatedPrompt);
  }

  async updatePrompt(prompt) {
    const response = await this.fetch(`/prompts/${prompt.id}`, {
      method: 'PUT',
      body: convertObjectToSnakeCase(prompt),
    });
    const updatedPrompt = await response.json();
    return mapApiPrompt(updatedPrompt);
  }
}

export { AiApiError, OCAISDK as default };
